/**
 * useViewOverlay + ViewOverlay — shared scroll-lock + dynamic positioning
 * for full-page View / Edit modals that sit below the TabBar.
 *
 * Problem solved
 * --------------
 * Many ERP pages render a `position: fixed` modal that should sit flush
 * below the sticky TabBar. Hard-coding `top-[38px]` looked correct on lg
 * screens with the welcome bar at top:0, but on smaller screens (welcome
 * bar hidden) or when the user had scrolled the list before opening the
 * modal, a gap appeared where the underlying list page scrolled through.
 *
 * What this hook does
 * -------------------
 *   1. Measures the actual bottom Y of `[data-testid="tab-bar"]` via
 *      `getBoundingClientRect()` so the modal sits flush regardless of
 *      layout / viewport / scroll state.
 *   2. Locks `document.body.style.overflow = "hidden"` for the modal's
 *      lifetime so the list page cannot scroll behind the gap.
 *   3. Restores the previous overflow + scroll position on unmount.
 *   4. Re-measures on window resize so the offset stays correct when
 *      the user resizes the browser.
 *
 * Two usage patterns
 * ------------------
 *   1. Hook directly inside an existing component:
 *        const topOffset = useViewOverlay();
 *        <div className="fixed ..." style={{ top: `${topOffset}px` }} />
 *
 *   2. Wrap your modal markup with the helper component (preferred for
 *      inline overlays inside a parent render):
 *        <ViewOverlay testid="rv-view-overlay">
 *          ...header + body markup...
 *        </ViewOverlay>
 */
import { useEffect, useState } from "react";

export function useViewOverlay() {
  const [topOffset, setTopOffset] = useState(88);  // sensible default

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    const prevScrollY = window.scrollY;
    document.body.style.overflow = "hidden";

    const measure = () => {
      const tb = document.querySelector('[data-testid="tab-bar"]');
      if (tb) {
        const rect = tb.getBoundingClientRect();
        setTopOffset(Math.max(0, Math.round(rect.bottom)));
      }
    };
    measure();
    window.addEventListener("resize", measure);

    return () => {
      window.removeEventListener("resize", measure);
      document.body.style.overflow = prevOverflow;
      window.scrollTo(0, prevScrollY);
    };
  }, []);

  return topOffset;
}

/**
 * ViewOverlay — convenience wrapper. Replaces the boilerplate
 *   `<div className="fixed left-0 lg:left-64 right-0 bottom-0 top-[38px] bg-white z-40 overflow-y-auto" data-testid="...">`
 * with
 *   `<ViewOverlay testid="...">`
 * and inherits the correct top offset + body scroll-lock automatically.
 *
 * Props
 * -----
 *   testid       — data-testid for E2E tests (e.g. "rv-view-overlay").
 *   className    — appended after the default className for custom
 *                  behaviour (e.g. `overflow-hidden flex flex-col`
 *                  for picker overlays).
 *   children     — modal content.
 */
export function ViewOverlay({ testid, className = "", onClose, children }) {
  const topOffset = useViewOverlay();
  // Optional Esc-to-close. Activated whenever the caller passes
  // `onClose`. Keeps the wrapper backward-compatible for callers that
  // already control dismissal via explicit buttons.
  useEffect(() => {
    if (!onClose) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const base = "fixed left-0 lg:left-64 right-0 bottom-0 bg-white z-40";
  // Default to vertical scroll inside the overlay. Callers that need a
  // different scroll/flex behaviour pass it via className (e.g. picker
  // dialogs use `overflow-hidden flex flex-col`).
  const overflow = className.includes("overflow-") ? "" : "overflow-y-auto";
  return (
    <div
      className={`${base} ${overflow} ${className}`.trim()}
      style={{ top: `${topOffset}px` }}
      data-testid={testid}
    >
      {children}
    </div>
  );
}
